import { useDroppable } from "@dnd-kit/core"
import { SortableContext, verticalListSortingStrategy } from "@dnd-kit/sortable"
import {
  CheckCircleOutlined,
  ForkOutlined,
  PlusOutlined
} from "@ant-design/icons"
import { Dropdown, Tag, Typography } from "antd"
import React from "react"

import { PlacedFieldCard } from "./FieldCard.tsx"
import type {
  FormFieldOption,
  RequirementGroup
} from "./form-field-requirements.types.ts"

const { Text, Title } = Typography

const GROUP_STYLES: Record<
  RequirementGroup,
  { icon: React.ReactNode; classes: string; badgeColor: string }
> = {
  all: {
    icon: <CheckCircleOutlined className="text-green-600" />,
    classes: "bg-green-50 border-green-200",
    badgeColor: "green"
  },
  any_one: {
    icon: <ForkOutlined className="text-amber-600" />,
    classes: "bg-amber-50 border-amber-200",
    badgeColor: "gold"
  }
}

interface RequirementGroupPanelProps {
  group: RequirementGroup
  title: string
  description: string
  fieldKeys: string[]
  labelFor: (fieldKey: string) => string
  addableFields: FormFieldOption[]
  onRemove: (fieldKey: string) => void
  onAdd: (fieldKey: string) => void
}

export function RequirementGroupPanel({
  group,
  title,
  description,
  fieldKeys,
  labelFor,
  addableFields,
  onRemove,
  onAdd
}: RequirementGroupPanelProps) {
  const style = GROUP_STYLES[group]
  const { setNodeRef, isOver } = useDroppable({
    id: `group:${group}`,
    data: { type: "group", group }
  })

  return (
    <div
      className={`rounded-lg border p-3 ${style.classes}`}
      data-testid={`requirement-group-${group}`}
    >
      <div className="mb-2 flex items-center justify-between">
        <div className="flex items-center gap-2">
          {style.icon}
          <Title className="m-0 text-sm" level={5}>
            {title}
          </Title>
          <Tag color={style.badgeColor}>{fieldKeys.length} fields</Tag>
        </div>
      </div>
      <Text className="mb-2 block text-xs" type="secondary">
        {description}
      </Text>

      <SortableContext
        items={fieldKeys.map((key) => `placed:${group}:${key}`)}
        strategy={verticalListSortingStrategy}
      >
        <div
          className={`flex min-h-[52px] flex-col gap-1.5 rounded-md border-2 border-dashed p-2 transition-colors ${
            isOver ? "border-current bg-white/60" : "border-transparent"
          }`}
          ref={setNodeRef}
        >
          {fieldKeys.map((key) => (
            <PlacedFieldCard
              fieldKey={key}
              group={group}
              key={key}
              label={labelFor(key)}
              onRemove={() => onRemove(key)}
            />
          ))}

          <Dropdown
            menu={{
              items: addableFields.map((field) => ({
                key: field.key,
                label: field.label,
                onClick: () => onAdd(field.key)
              })),
              style: { maxHeight: 280, overflowY: "auto" }
            }}
            disabled={addableFields.length === 0}
            trigger={["click"]}
          >
            <button
              className="flex items-center justify-center gap-1.5 rounded-md border border-dashed border-gray-300 bg-white/60 py-2 text-xs text-gray-500 hover:border-gray-400 hover:text-gray-700 disabled:cursor-not-allowed disabled:opacity-50"
              disabled={addableFields.length === 0}
              type="button"
            >
              <PlusOutlined /> Drop fields here or click to add
            </button>
          </Dropdown>
        </div>
      </SortableContext>
    </div>
  )
}
